import React, { useEffect, useState, useRef } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArrowLeft, Play, Phone, MapPin, Calendar, Volume2, VolumeX } from 'lucide-react';
export function HonorableMusicPage() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [audioStarted, setAudioStarted] = useState(false);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0.5;
    audio.loop = true;
    // Attempt to autoplay
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.then(() => setAudioStarted(true)).catch(() => {
        // Autoplay blocked - will play on first user interaction
        const startOnInteraction = () => {
          audio.play().then(() => setAudioStarted(true)).catch(() => {});
          document.removeEventListener('click', startOnInteraction);
          document.removeEventListener('scroll', startOnInteraction);
        };
        document.addEventListener('click', startOnInteraction, {
          once: true
        });
        document.addEventListener('scroll', startOnInteraction, {
          once: true
        });
      });
    }
    // Fade audio out on scroll
    const handleScroll = () => {
      if (!audio || isMuted) return;
      const scrollY = window.scrollY;
      const fadeStart = 200;
      const fadeEnd = 800;
      if (scrollY < fadeStart) {
        audio.volume = 0.5;
      } else if (scrollY > fadeEnd) {
        audio.volume = 0;
      } else {
        const progress = (scrollY - fadeStart) / (fadeEnd - fadeStart);
        audio.volume = 0.5 * (1 - progress);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      audio.pause();
    };
  }, [isMuted]);
  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isMuted) {
      audio.volume = 0.5;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };
  return <div className="min-h-screen font-sans" style={{
    backgroundColor: '#0D121D'
  }} data-id="element-198">
      <audio ref={audioRef} src="https://cdn.magicpatterns.com/uploads/e4ZF1Xcia2BBPx4J8edWyT/Bumpa_Hertz_Riddim.mp3" data-id="element-199" />

      <Header data-id="element-200" />

      {/* Mute toggle button - fixed */}
      <button onClick={toggleMute} className="fixed bottom-8 right-8 z-50 w-14 h-14 rounded-full bg-green-500 hover:bg-green-400 text-black shadow-lg shadow-green-500/50 flex items-center justify-center transition-colors" aria-label={isMuted ? 'Unmute audio' : 'Mute audio'} data-id="element-201">
        {isMuted ? <VolumeX className="w-6 h-6" data-id="element-202" /> : <Volume2 className="w-6 h-6" data-id="element-203" />}
      </button>

      <main className="pt-32 pb-24" data-id="element-204">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-205">
          <a href="/saint-lucia" className="inline-flex items-center text-slate-300 hover:text-white transition-colors mb-12" data-id="element-206">
            <ArrowLeft className="w-5 h-5 mr-2" data-id="element-207" />
            Back to Saint Lucia
          </a>

          {/* Hero Section with Artist Photo */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24" data-id="element-208">
            {/* Artist Photo with green glow */}
            <div className="relative order-2 lg:order-1" data-id="element-209">
              <div className="absolute -inset-4 bg-green-500/30 blur-3xl rounded-full" data-id="element-210" />
              <div className="relative rounded-3xl overflow-hidden border-2 border-green-500/50" data-id="element-211">
                <img src="https://cdn.magicpatterns.com/uploads/vcnsXBbGS4zPtRc55VDBKi/Honorable_Music_-Brand_Image_1.png" alt="Honorable Music Artist" className="w-full h-auto" data-id="element-212" />
              </div>
            </div>

            {/* Artist Info */}
            <div className="order-1 lg:order-2 text-center lg:text-left" data-id="element-213">
              <div className="mb-6 flex justify-center lg:justify-start" data-id="element-214">
                <div className="bg-white rounded-2xl p-4 inline-block" data-id="element-215">
                  <img src="https://cdn.magicpatterns.com/uploads/kNU33mM35Pd4B3EEWnk3ED/Honorable_Music_Logo-01.png" alt="Honorable Music Logo" className="h-24 w-auto" data-id="element-216" />
                </div>
              </div>

              <div className="inline-block px-4 py-1.5 rounded-full bg-green-500/20 text-green-400 text-sm font-bold mb-4 uppercase tracking-wider" data-id="element-217">
                Soca • Saint Lucia
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight" data-id="element-218">
                <span className="text-green-400" data-id="element-219">Honorable</span>
                <br data-id="element-220" />
                Music
              </h1>

              <p className="text-2xl text-slate-300 mb-8 italic" data-id="element-221">
                "Start a vibe. Book a performance."
              </p>

              <p className="text-lg text-slate-400 leading-relaxed mb-8" data-id="element-222">
                A new Soca artist rising out of Saint Lucia, bringing infectious
                rhythms and unforgettable performances to every stage.
              </p>

              <a href="#book" className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-bold rounded-full text-black bg-green-400 hover:bg-green-300 transition-colors shadow-lg shadow-green-500/30" data-id="element-223">
                <Calendar className="w-5 h-5" data-id="element-224" />
                Book a Performance
              </a>
            </div>
          </div>

          {/* Hit Singles Section */}
          <div className="mb-24" data-id="element-225">
            <div className="text-center mb-12" data-id="element-226">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-3" data-id="element-227">
                Two Hit Singles
              </h2>
              <p className="text-lg text-slate-400" data-id="element-228">
                Stream the tracks that are taking over the islands
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-229">
              {/* Bon Maten */}
              <a href="https://www.youtube.com/watch?v=lp6UXuE4qBY" target="_blank" rel="noopener noreferrer" className="group relative bg-gradient-to-br from-green-900/40 to-slate-900/60 backdrop-blur rounded-3xl border border-green-500/30 hover:border-green-400 transition-all p-8 overflow-hidden" data-id="element-230">
                <div className="absolute -top-12 -right-12 w-40 h-40 bg-green-500/20 rounded-full blur-3xl group-hover:bg-green-400/30 transition-colors" data-id="element-231" />
                <div className="relative" data-id="element-232">
                  <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-233">
                    <Play className="w-7 h-7 text-black ml-1" fill="currentColor" data-id="element-234" />
                  </div>
                  <div className="text-xs text-green-400 font-bold uppercase tracking-widest mb-2" data-id="element-235">
                    Hit Single #1
                  </div>
                  <h3 className="text-3xl font-extrabold text-white mb-2" data-id="element-236">
                    Bon Maten
                  </h3>
                  <p className="text-slate-300 mb-6" data-id="element-237">
                    Watch on YouTube • Open in new tab
                  </p>
                  <span className="text-green-400 font-bold inline-flex items-center gap-1" data-id="element-238">
                    Watch Now →
                  </span>
                </div>
              </a>

              {/* Marry Warna */}
              <a href="https://www.youtube.com/watch?v=aweHXDXcNyc" target="_blank" rel="noopener noreferrer" className="group relative bg-gradient-to-br from-green-900/40 to-slate-900/60 backdrop-blur rounded-3xl border border-green-500/30 hover:border-green-400 transition-all p-8 overflow-hidden" data-id="element-239">
                <div className="absolute -top-12 -right-12 w-40 h-40 bg-green-500/20 rounded-full blur-3xl group-hover:bg-green-400/30 transition-colors" data-id="element-240" />
                <div className="relative" data-id="element-241">
                  <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-242">
                    <Play className="w-7 h-7 text-black ml-1" fill="currentColor" data-id="element-243" />
                  </div>
                  <div className="text-xs text-green-400 font-bold uppercase tracking-widest mb-2" data-id="element-244">
                    Hit Single #2
                  </div>
                  <h3 className="text-3xl font-extrabold text-white mb-2" data-id="element-245">
                    Marry Warna
                  </h3>
                  <p className="text-slate-300 mb-6" data-id="element-246">
                    Watch on YouTube • Open in new tab
                  </p>
                  <span className="text-green-400 font-bold inline-flex items-center gap-1" data-id="element-247">
                    Watch Now →
                  </span>
                </div>
              </a>
            </div>
          </div>

          {/* Booking / Contact Section */}
          <div id="book" className="bg-gradient-to-br from-green-600/20 via-slate-900/40 to-slate-900/60 backdrop-blur rounded-3xl border border-green-500/30 p-12 max-w-5xl mx-auto" data-id="element-248">
            <div className="text-center mb-10" data-id="element-249">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-3" data-id="element-250">
                Book the Vibe
              </h2>
              <p className="text-lg text-slate-300" data-id="element-251">
                Reach out directly to book Honorable Music for your next event
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-252">
              <a href="tel:+13475836724" className="bg-white/5 hover:bg-white/10 border border-slate-700 hover:border-green-400 rounded-2xl p-8 flex items-start gap-4 transition-all" data-id="element-253">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0" data-id="element-254">
                  <MapPin className="w-6 h-6 text-green-400" data-id="element-255" />
                </div>
                <div data-id="element-256">
                  <div className="text-sm text-green-400 font-bold uppercase tracking-wider mb-1" data-id="element-257">
                    New York
                  </div>
                  <div className="text-2xl font-bold text-white flex items-center gap-2" data-id="element-258">
                    <Phone className="w-5 h-5" data-id="element-259" />
                    +1 (347) 583-6724
                  </div>
                </div>
              </a>

              <a href="tel:+17587198554" className="bg-white/5 hover:bg-white/10 border border-slate-700 hover:border-green-400 rounded-2xl p-8 flex items-start gap-4 transition-all" data-id="element-260">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0" data-id="element-261">
                  <MapPin className="w-6 h-6 text-green-400" data-id="element-262" />
                </div>
                <div data-id="element-263">
                  <div className="text-sm text-green-400 font-bold uppercase tracking-wider mb-1" data-id="element-264">
                    Saint Lucia
                  </div>
                  <div className="text-2xl font-bold text-white flex items-center gap-2" data-id="element-265">
                    <Phone className="w-5 h-5" data-id="element-266" />
                    +1 (758) 719-8554
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-267" />
    </div>;
}